import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAppLogo } from '../hooks/useAppLogo';
import { authAPI } from '../services/api';
import '../styles/AuthPages.css';

const APP_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(getSystemTheme);
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = APP_LOGO || appLogoUrl;
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'night' : 'day');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. No token provided.');
        setValidating(false);
        return;
      }

      try {
        const response = await authAPI.verifyResetToken(token);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email || '');
        } else {
          setError('Invalid or expired reset link.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired reset link.');
      } finally {
        setValidating(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (validating) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Verifying reset link...</p>
        </div>
      );
    }

    if (success) {
      return (
        <div className="success-message">
          <div className="success-icon">
            <Icon name="celebration" color="success" darkMode={theme === 'night'} size={48} />
          </div>
          <h3>Password Reset Successful!</h3>
          <p>Your password has been successfully updated.</p>
          <p style={{ marginTop: '10px', color: '#666' }}>
            You can now log in with your new password.
          </p>
          <div className="auth-footer" style={{ marginTop: '30px' }}>
            <button 
              className="btn btn-primary btn-block"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      );
    }

    if (!tokenValid) {
      return (
        <div className="error-state">
          <div className="error-icon">
            <Icon name="warning" color="error" darkMode={theme === 'night'} size={48} />
          </div>
          <h3>Invalid Reset Link</h3>
          <p>{error || 'This password reset link is invalid or has expired.'}</p>
          <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
            Please request a new password reset link.
          </p>
          <div className="auth-footer" style={{ marginTop: '30px' }}>
            <Link to="/forgot-password" className="btn btn-primary btn-block">
              Request New Link
            </Link>
            <p style={{ marginTop: '15px' }}>
              <Link to="/login">Back to Login</Link>
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        {userEmail && (
          <p style={{ color: '#666', marginBottom: '20px', textAlign: 'center' }}>
            Resetting password for: <strong>{userEmail}</strong>
          </p>
        )}
        
        {error && <div className="error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter new password"
              autoComplete="new-password"
              minLength={6}
            />
            <small style={{ color: '#888', marginTop: '5px', display: 'block' }}>
              Must be at least 6 characters
            </small>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password? <Link to="/login">Login here</Link>
          </p>
        </div>
      </>
    );
  };

  return (
    <div className={`auth-page theme-${theme}`}>
      <div className="auth-container">
        <div className="auth-logo">
          {logoUrl && (
            <div className="auth-logo-shell">
              <div className="auth-logo-glow"></div>
              <img src={logoUrl} alt="Frame-Book logo" className="auth-logo-image" />
            </div>
          )}
          <h1>Frame-Book</h1>
          <p>Bike Passport & Service History</p>
        </div>

        <div className="auth-card">
          <h2>Set New Password</h2>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;

